import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState } from './authSlice';
import {
  ApproveTranslationRequest,
  CreateNewTranslationRecordRequest,
  CreateNewTranslationRecordResponse,
  DeactivateTranslationRequest,
  DeactivateTranslationResponse,
  DeclineTranslationRequest,
  FetchTranslationsRequest,
  ProposeTranslationEditRequest,
  ProposeTranslationEditResponse,
  UpdateTranslationRequest
} from '../../types/translationTypes';
import { translationApi } from '../../services';

export enum TranslationStatus {
  PUBLISHED = 'Published',
  NEEDS_REVIEW = 'NeedsReview',
  DEACTIVATED = 'Deactivated',
}

export const TranslationStatusStrings: Record<TranslationStatus, string> = {
  [TranslationStatus.PUBLISHED]: 'Published',
  [TranslationStatus.NEEDS_REVIEW]: 'Needs Review',
  [TranslationStatus.DEACTIVATED]: 'Deactivated',
}

export enum TranslationReviewStatus {
  APPROVED = 'Approved',
  DECLINED = 'Declined',
  NEEDS_REVIEW = 'NeedsReview',
}


type Metadata = {
  userAgent: string;
  ipAddress: string;
  sessionId: string;
};

type ChangeDetail = {
  field: string;
  oldValue: string;
  newValue: string;
  changeReason: string;
};

type ChangeHistoryItem = {
  changeId: string;
  changeType: string;
  timestamp: string; // ISO date string
  changedBy: string;
  changes: ChangeDetail[];
  comments: string;
  metadata: Metadata;
};

export type Translation = {
  translationRecordId: string,
  originalString: string,
  translatedString: string,
  lastUpdatedBy: string,
  date: Date
}

export type TranslationDetail = {
  id: string;
  lang: string;
  translatedLanguage: string;
  key: string;
  approvedValue: string;
  status: TranslationStatus;
  valueInReview: string;
  reviewStatus: TranslationReviewStatus;
  version: number;
  createdBy: string;
  createdOn: string; // ISO date string
  lastModifiedBy: string;
  lastModifiedOn: string; // ISO date string
  tags: string[];
  changeHistory: ChangeHistoryItem[];
};

interface TranslationsState {
  translations: Translation[],
  queries: {
    nextToken: null | string,
    page: number,
    pageSize: number,
    totalPages: number,
    totalRows: number,
    searchQuery: string,
    statusFilter: TranslationStatus,
    loading: boolean,
    error: string | null,
  },
  createRecord: {
    loading: boolean,
    error: string | null,
    success: string | null,
  },
  edit: {
    dialogOpen: boolean,
    translation: Translation | null,
    loading: boolean,
    error: string | null,
    success: string | null,
  },
  approvalDecline: {
    loading: boolean,
    error: string | null,
    success: string | null,
  },
  deactivate: {
    loading: boolean,
    error: string | null,
    success: string | null,
  }
}

// --- ASYNC THUNKS ---

// Deactivate translation thunks
export const deactivateTranslationAsync = createAsyncThunk<DeactivateTranslationResponse, DeactivateTranslationRequest>('translations/deactivateTranslation', async (request, { rejectWithValue }) => {
  const response = await translationApi.deactivateTranslation(request);
  if (response.status !== 'Success') {
    return rejectWithValue(response.message);
  }
  return response;
});

export const deactivateTranslationAndRefresh = createAsyncThunk<void, DeactivateTranslationRequest>('translations/deactivateTranslationAndRefresh', async (request, { dispatch }) => {
  await dispatch(deactivateTranslationAsync(request));
  await dispatch(refreshCurrentPage());
});

// Async thunks for API calls
export const fetchTranslations = createAsyncThunk(
  'translations/fetchTranslations',
  async (filters: FetchTranslationsRequest, { getState }) => {
    const state = getState() as { translations: TranslationsState, auth: AuthState };
    const { searchQuery, statusFilter, pageSize } = state.translations.queries;
    if (!state.auth.user) {
      throw new Error('User not authenticated');
    }

    const response = await translationApi.fetchTranslations({
      ...filters,
      searchText: searchQuery,
      status: statusFilter,
      pageSize,
    }, {
      'Authorization': `Bearer ${state.auth.user?.jwtIdToken || ''}`,
    });

    return response;
  }
);

// Thunk to refresh current page (after create/update operations)
export const refreshCurrentPage = createAsyncThunk(
  'translations/refreshCurrentPage',
  async (_, { getState, dispatch }) => {
    const state = getState() as { translations: TranslationsState };
    const { searchQuery, statusFilter, page, pageSize, nextToken } = state.translations.queries;

    dispatch(fetchTranslations({
      language: 'es',
      searchText: searchQuery,
      status: statusFilter,
      pageNumber: page,
      pageSize,
      continuationToken: nextToken
    }));
  }
);

// Compound thunk for updating translation and refreshing
export const updateTranslationAndRefresh = createAsyncThunk('translations/updateTranslationAndRefresh', async (request: UpdateTranslationRequest, { dispatch }) => {
  await dispatch(updateTranslationAsync(request));
  await dispatch(refreshCurrentPage());
});

// Compound thunk for creating translation and refreshing
export const createTranslationAndRefresh = createAsyncThunk(
  'translations/createTranslationAndRefresh',
  async (request: CreateNewTranslationRecordRequest, { dispatch }) => {
    await dispatch(createNewTranslationRecord(request));
    await dispatch(refreshCurrentPage());
  }
);

export const updateTranslationAsync = createAsyncThunk('translations/updateTranslation', async (request: UpdateTranslationRequest) => {
  const response = await translationApi.updateTranslation(request);
  return response;
});

export const createNewTranslationRecord = createAsyncThunk<
  CreateNewTranslationRecordResponse,
  CreateNewTranslationRecordRequest,
  { rejectValue: string }
>(
  'translations/createNewTranslationRecord',
  async (request) => translationApi.createNewTranslationRecord(request)
);

// Thunk for approving a translation
export const approveTranslation = createAsyncThunk(
  'translations/approveTranslation',
  async (request: ApproveTranslationRequest) => {
    const response = await translationApi.approveTranslation(request);
    return response;
  }
);

// Thunk for declining a translation  
export const declineTranslation = createAsyncThunk(
  'translations/declineTranslation',
  async (request: DeclineTranslationRequest) => {
    const response = await translationApi.declineTranslation(request);
    return response;
  }
);

// Compound thunk for approving translation and refreshing current page
export const approveTranslationAndRefresh = createAsyncThunk(
  'translations/approveTranslationAndRefresh',
  async (request: ApproveTranslationRequest, { dispatch }) => {
    const result = await dispatch(approveTranslation(request)).unwrap();
    await dispatch(refreshCurrentPage());
    return result;
  }
);

// Compound thunk for declining translation and refreshing current page
export const declineTranslationAndRefresh = createAsyncThunk(
  'translations/declineTranslationAndRefresh',
  async (request: DeclineTranslationRequest, { dispatch }) => {
    const result = await dispatch(declineTranslation(request)).unwrap();
    await dispatch(refreshCurrentPage());
    return result;
  }
);

export const proposeTranslationEdit = createAsyncThunk<ProposeTranslationEditResponse, ProposeTranslationEditRequest>(
  'translations/ProposeTranslationEdit',
  async (request) => {
    const response = await translationApi.proposeTranslationEdit(request);
    return response;
  }
);

// Compound thunk for proposing translation edit and refreshing current page
export const proposeTranslationEditAndRefresh = createAsyncThunk(
  'translations/proposeTranslationEditAndRefresh',
  async (request: ProposeTranslationEditRequest, { dispatch }) => {
    const result = await dispatch(proposeTranslationEdit(request)).unwrap();
    await dispatch(refreshCurrentPage());
    return result;
  }
);

const initialState: TranslationsState = {
  translations: [], // List of translations
  queries: { // State for queries and filters
    nextToken: null, // Pagination token
    page: 1, // Current page number
    pageSize: 25, // Items per page
    totalPages: 0, // Total number of pages
    totalRows: 0, // Total number of rows
    searchQuery: '', // Search text
    statusFilter: TranslationStatus.PUBLISHED, // Status filter
    loading: false, // Loading state
    error: null, // Error message
  },
  createRecord: { // State for creating new translation record
    loading: false, // Loading state
    error: null, // Error message
    success: null, // Success message
  },
  edit: { // State for editing translation
    dialogOpen: false, // Is edit dialog open
    translation: null, // Currently editing translation
    loading: false, // Loading state
    error: null, // Error message
    success: null, // Success message
  },
  approvalDecline: { // State for approve/decline actions
    loading: false, // Loading state
    error: null, // Error message
    success: null, // Success message
  },
  deactivate: { // State for deactivate action
    loading: false, // Loading state
    error: null, // Error message
    success: null, // Success message
  }
};

const translationsSlice = createSlice({
  name: 'translations',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.queries.searchQuery = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<TranslationStatus>) => {
      state.queries.statusFilter = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.queries.pageSize = action.payload;
      state.queries.page = 1; // Reset to first page when changing page size
    },
    setPageNumber: (state, action: PayloadAction<number>) => {
      state.queries.page = action.payload;
    },
    openEditDialog: (state, action: PayloadAction<Translation>) => {
      state.edit.translation = action.payload;
      state.edit.dialogOpen = true;
      state.edit.loading = false;
      state.edit.error = null;
    },
    closeEditDialog: (state) => {
      state.edit.translation = null;
      state.edit.dialogOpen = false;
      state.edit.loading = false;
      state.edit.error = null;
    },
     clearCreateRecord: (state) => {
      state.createRecord.loading = false;
      state.createRecord.error = null;
      state.createRecord.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch translations
      .addCase(fetchTranslations.pending, (state) => {
        state.queries.loading = true;
        state.queries.error = null;
      })
      .addCase(fetchTranslations.fulfilled, (state, action) => {
        state.queries.loading = false;
        state.translations = action.payload.results;
        state.queries.totalRows = action.payload.totalRows;
        state.queries.error = null;
        // Calculate total pages
        state.queries.totalPages = Math.ceil(action.payload.totalRows / state.queries.pageSize);
        // state.queries.nextToken = action.payload.continuationToken || null;
      })
      .addCase(fetchTranslations.rejected, (state, action) => {
        state.queries.loading = false;
        state.queries.error = action.error.message || 'Failed to fetch translations';
      })
      // Create NEW translation record (real API)
      .addCase(createNewTranslationRecord.pending, (state) => {
        state.createRecord.loading = true;
        state.createRecord.error = null;
        state.createRecord.success = null;
      })
      .addCase(createNewTranslationRecord.fulfilled, (state) => {
        state.createRecord.loading = false;
        state.createRecord.error = null;
        state.createRecord.success = 'New translation submitted';
      })
      .addCase(createNewTranslationRecord.rejected, (state, action) => {
        state.createRecord.loading = false;
        state.createRecord.error =
          (action.payload as string | undefined) ??
          action.error?.message ??
          'Failed to create new translation';
      })
      // Deactivate translation
      .addCase(deactivateTranslationAsync.pending, (state: TranslationsState) => {
        state.deactivate.loading = true;
        state.deactivate.error = null;
        state.deactivate.success = null;
      })
      .addCase(deactivateTranslationAsync.fulfilled, (state: TranslationsState) => {
        state.deactivate.loading = false;
        state.deactivate.error = null;
        state.deactivate.success = 'Translation deactivated';
      })
      .addCase(deactivateTranslationAsync.rejected, (state: TranslationsState, action: PayloadAction<any>) => {
        state.deactivate.loading = false;
        state.deactivate.error = action.payload || 'Failed to deactivate translation';
      })
      //Approve translation
      .addCase(approveTranslation.pending, (state) => {
        state.approvalDecline.loading = true;
        state.approvalDecline.error = null;
        state.approvalDecline.success = null;
      })
      .addCase(approveTranslation.fulfilled, (state) => {
        state.approvalDecline.loading = false;
        state.approvalDecline.error = null;
        state.approvalDecline.success = 'Translation approved';
      })
      .addCase(approveTranslation.rejected, (state, action) => {
        state.approvalDecline.loading = false;
        state.approvalDecline.error = action.error.message ||  'Failed to approve translation';
      })
      //Decline translation
      .addCase(declineTranslation.pending, (state) => {
        state.approvalDecline.loading = true;
        state.approvalDecline.error = null;
        state.approvalDecline.success = null;
      })
      .addCase(declineTranslation.fulfilled, (state) => {
        state.approvalDecline.loading = false;
        state.approvalDecline.error = null;
        state.approvalDecline.success = 'Translation declined';
      })
      .addCase(declineTranslation.rejected, (state, action) => {
        state.approvalDecline.loading = false;
        state.approvalDecline.error = action.error.message || 'Failed to decline translation';
      })
      //Propose translation
      .addCase(proposeTranslationEdit.pending, (state) => {
        state.edit.loading = true;
        state.edit.error = null;
        state.edit.success = null;
      })
      .addCase(proposeTranslationEdit.fulfilled, (state) => {
        state.edit.loading = false;
        state.edit.error = null;
        state.edit.success = 'Translation edit proposed successfully';
      })
      .addCase(proposeTranslationEdit.rejected, (state, action) => {
        state.edit.loading = false;
        state.edit.error = action.error.message || 'Failed to propose translation edit';
      })
      //Propose translation and refresh
      .addCase(proposeTranslationEditAndRefresh.pending, (state) => {
        state.edit.loading = true;
        state.edit.error = null;
        state.edit.success = null;
      })
      .addCase(proposeTranslationEditAndRefresh.fulfilled, (state) => {
        state.edit.loading = false;
        state.edit.error = null;
        state.edit.success = 'Translation edit proposed successfully';
      })
      .addCase(proposeTranslationEditAndRefresh.rejected, (state, action) => {
        state.edit.loading = false;
        state.edit.error = action.error.message || 'Failed to propose translation edit';
      });
  },
});

export const {
  setSearchQuery,
  setStatusFilter,
  setPageSize,
  setPageNumber,
  openEditDialog,
  closeEditDialog,
  clearCreateRecord
} = translationsSlice.actions;

export default translationsSlice.reducer;
